import { useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ConversationKanbanBoardAggregates,
  ConversationKanbanCard,
  ConversationKanbanColumnQuery,
  ConversationKanbanLaneId,
  ConversationKanbanQuery,
  KanbanBoardResponse,
  KanbanSortMode,
} from '@troia-v3/types';
import { useCallback, useState } from 'react';

import { getAttendanceKanbanBoard, getAttendanceKanbanColumn } from '@/modules/chat/services/chatService';

interface UseAttendanceKanbanParams {
  search?: string;
  filters?: ConversationKanbanQuery['filters'];
  windowSize?: number;
  sortMode?: KanbanSortMode;
  /** Permite condicionar o fetch (ex.: aba kanban não ativa). Default `true`. */
  enabled?: boolean;
}

type AttendanceKanbanBoard = KanbanBoardResponse<ConversationKanbanCard, ConversationKanbanBoardAggregates>;

/**
 * Board kanban de atendimentos com janelas por lane (5 raias fixas — ai,
 * queue, inService, transferred, closed — derivadas server-side, sem
 * pipelineId/funnelId). Counts/busca vêm do backend; `loadMore` anexa
 * páginas por cursor; `applyCardMove` faz cirurgia de cache pós-move (sem
 * refetch do board — preserva o scroll das lanes). Espelho de
 * `useTicketsKanban` (SP3) — Task 5 do SP4.
 *
 * Diferenças de `useTicketsKanban`/`useLeadsKanban`: SEM `applyCardReorder`
 * (não há modo `manual` no kanban de atendimentos — `CONVERSATION_KANBAN_SORT_MODES`
 * não inclui 'manual'), SEM contexto obrigatório tipo `pipelineId`/`funnelId`
 * (as 5 lanes são fixas), `refetchInterval: 60_000` para paridade com o
 * polling atual de `useAttendanceData` (D12 do spec SP4).
 *
 * `applyCardMove` NÃO tenta sintetizar campos do card pra refletir a lane
 * destino (diferente de tickets/leads, que setam `stageId`/`stepId` — a lane
 * de uma conversa é DERIVADA server-side via predicados sobre
 * status/agentId/agentStatus/closeReason, não um campo persistido). O
 * caller deve passar o `card` já com os campos atualizados (ex.: a resposta
 * da mutation de `/conversations/:id/start`, que muda `status` pra 'active'
 * e `assigneeId`). Aggregates.total do board NÃO muda em move entre lanes —
 * só os counts das colunas de origem/destino.
 */
export function useAttendanceKanban({
  search,
  filters,
  windowSize = 50,
  sortMode = 'created',
  enabled = true,
}: UseAttendanceKanbanParams) {
  const queryClient = useQueryClient();
  const queryKey = ['attendance-kanban', search ?? '', filters ?? {}, sortMode] as const;

  const query = useQuery({
    queryKey,
    queryFn: () =>
      getAttendanceKanbanBoard({
        ...(search ? { search } : {}),
        ...(filters ? { filters } : {}),
        windowSize,
        sortMode,
      }),
    enabled,
    refetchInterval: 60_000,
    refetchOnWindowFocus: false,
  });

  const [loadingColumns, setLoadingColumns] = useState<Record<string, boolean>>({});
  const [errorColumns, setErrorColumns] = useState<Record<string, boolean>>({});

  const loadMore = useCallback(
    async (laneId: string) => {
      const data = queryClient.getQueryData<AttendanceKanbanBoard>(queryKey);
      const column = data?.columns.find((c) => c.columnId === laneId);
      if (!column?.nextCursor || loadingColumns[laneId]) return;

      setLoadingColumns((prev) => ({ ...prev, [laneId]: true }));
      setErrorColumns((prev) => ({ ...prev, [laneId]: false }));
      try {
        const page = await getAttendanceKanbanColumn({
          laneId: laneId as ConversationKanbanLaneId,
          cursor: column.nextCursor,
          ...(search ? { search } : {}),
          ...(filters ? { filters } : {}),
          windowSize,
          sortMode,
        } satisfies ConversationKanbanColumnQuery);
        queryClient.setQueryData<AttendanceKanbanBoard>(queryKey, (old) =>
          old
            ? {
                ...old,
                columns: old.columns.map((c) => {
                  if (c.columnId !== laneId) return c;
                  // Dedupe por id: um card movido via applyCardMove (inserido no topo
                  // da lane destino) já persiste no DB com o novo status/lane antes desse
                  // fetch resolver — se ele cair na janela da próxima página por cursor,
                  // o backend o retorna de novo e duplicaria o item.
                  const seen = new Set(c.items.map((i) => i.id));
                  return {
                    ...c,
                    items: [...c.items, ...page.items.filter((i) => !seen.has(i.id))],
                    nextCursor: page.nextCursor,
                    count: page.count,
                    totalValue: page.totalValue,
                  };
                }),
              }
            : old,
        );
      } catch (error) {
        const status = (error as { response?: { status?: number } }).response?.status;
        if (status === 422) {
          // Cursor inválido/legado (formato de outro sortMode): re-tentar o
          // mesmo cursor é beco sem saída — limpa e refaz o board (espelho SP2/SP3).
          queryClient.setQueryData<AttendanceKanbanBoard>(queryKey, (old) =>
            old
              ? { ...old, columns: old.columns.map((c) => (c.columnId === laneId ? { ...c, nextCursor: null } : c)) }
              : old,
          );
          void queryClient.invalidateQueries({ queryKey: ['attendance-kanban'] });
        } else {
          setErrorColumns((prev) => ({ ...prev, [laneId]: true }));
        }
      } finally {
        setLoadingColumns((prev) => ({ ...prev, [laneId]: false }));
      }
    },
    [queryClient, queryKey, search, filters, windowSize, sortMode, loadingColumns],
  );

  const applyCardMove = useCallback(
    (card: ConversationKanbanCard, fromLaneId: string, toLaneId: string) => {
      queryClient.setQueryData<AttendanceKanbanBoard>(queryKey, (old) => {
        if (!old) return old;
        return {
          ...old,
          columns: old.columns.map((c) => {
            if (c.columnId === fromLaneId) {
              return {
                ...c,
                items: c.items.filter((i) => i.id !== card.id),
                count: Math.max(0, c.count - 1),
              };
            }
            if (c.columnId === toLaneId) {
              return {
                ...c,
                items: [card, ...c.items],
                count: c.count + 1,
              };
            }
            return c;
          }),
        };
      });
    },
    [queryClient, queryKey],
  );

  const invalidate = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['attendance-kanban'] });
  }, [queryClient]);

  return {
    boardData: query.data,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
    loadMore,
    applyCardMove,
    invalidate,
    loadingColumns,
    errorColumns,
  };
}
